/**
 * Local on-device issue log (JSON array). Entries older than 30 days are pruned on each write.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { IssueReportEntry } from './issueReportTypes';

const STORAGE_KEY = '@v3_issue_report/log_v1';
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export async function readIssueReportLog(): Promise<IssueReportEntry[]> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as IssueReportEntry[];
  } catch {
    return [];
  }
}

function pruneExpiredEntries(entries: IssueReportEntry[], now_ms: number): IssueReportEntry[] {
  const cutoff = now_ms - RETENTION_MS;
  return entries.filter(entry => {
    const ts = Date.parse(entry.reported_at);
    return Number.isFinite(ts) && ts >= cutoff;
  });
}

export async function appendIssueReportEntry(entry: IssueReportEntry): Promise<void> {
  try {
    const now_ms = Date.now();
    const existing = await readIssueReportLog();
    const pruned = pruneExpiredEntries(existing, now_ms);
    pruned.push(entry);
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
  } catch {
    // Never block the app on local logging failures.
  }
}

export async function clearIssueReportLog(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
